import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

// Import shared locale files
import deCommon from './locales/de/common.json'
import deErrors from './locales/de/errors.json'
import enCommon from './locales/en/common.json'
import enErrors from './locales/en/errors.json'

export const defaultNS = 'common'

export const sharedResources = {
  de: {
    common: deCommon,
    errors: deErrors,
  },
  en: {
    common: enCommon,
    errors: enErrors,
  },
} as const

export type SharedResources = typeof sharedResources

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslationResource = Record<string, any>

/**
 * Initialize i18next with shared resources.
 * Apps can extend with app-specific namespaces.
 */
export function initI18n(options?: {
  lng?: string
  debug?: boolean
  additionalResources?: Record<string, Record<string, TranslationResource>>
  additionalNamespaces?: string[]
}) {
  const {
    lng = 'de',
    debug = false,
    additionalResources = {},
    additionalNamespaces = [],
  } = options ?? {}

  // Merge shared resources with app-specific resources
  const resources = { ...sharedResources }
  for (const [lang, namespaces] of Object.entries(additionalResources)) {
    if (!resources[lang as keyof typeof resources]) {
      ;(resources as Record<string, Record<string, unknown>>)[lang] = {}
    }
    Object.assign(resources[lang as keyof typeof resources], namespaces)
  }

  const namespaces = Array.from(new Set(['common', 'errors', ...additionalNamespaces]))

  i18n.use(initReactI18next).init({
    lng,
    fallbackLng: 'en',
    debug,
    defaultNS,
    ns: namespaces,
    resources,
    interpolation: {
      escapeValue: false, // React already escapes
    },
  })

  return i18n
}

/**
 * Translate an error code to a localized message.
 * Falls back to HTTP status code message if specific code not found.
 */
export function translateError(code: string | undefined, status: number): string {
  if (code && i18n.exists(`errors:${code}`)) {
    return i18n.t(`errors:${code}`)
  }
  if (i18n.exists(`errors:${status}`)) {
    return i18n.t(`errors:${status}`)
  }
  return `HTTP ${status}`
}

// Re-export commonly used items from react-i18next
export { useTranslation, Trans, I18nextProvider } from 'react-i18next'
export type { TFunction } from 'i18next'
export { i18n }
